// ============================================================================
// Integración con Finanzas (sección 3 del diseño, PR 4 de la épica #1638)
// ============================================================================
// Reglas puras; quien escribe es el servidor (`modules/assets/assetsFinance`).
//
//   · D3: una compra = un gasto, con id determinista `exp_asset_{purchaseId}`.
//   · D4: al presupuesto va el COSTO DE COMPRA en la FECHA DE COMPRA, en la
//     categoría de `asset_settings.expenseCategory` (por omisión `hardware`).
//     La depreciación y el valor en libros son informativos: no generan gastos.
//   · D5: el gasto nace `por_revisar`; Finanzas lo confirma.
//   · D2: la integración se enciende por compañía (`financeSync: "borrador"`).
//     Donde Finanzas es interna de la plataforma (CC-CONSOLE: las Finanzas son
//     de Coding Cloud), sólo la compañía de Gastos puede integrarse, y es la
//     única que nace encendida.
// ============================================================================

import type { AssetAttachment, AssetPurchase, InvoiceRef } from "./types";
import type { AssetCategory, FinanceSyncMode, Label } from "./vocab";
import { BASE_CURRENCY, isAssetCategory, isFinanceSyncMode } from "./vocab";
import { purchaseExpenseId } from "./identifiers";
import { MAX_USEFUL_LIFE_MONTHS } from "./depreciation";
import type { ValidationIssue, ValidationResult } from "./validation";

/** Id de categoría de Gastos (mismo patrón que `CATEGORY_ID_RE` de `@torre/expenses`). */
const EXPENSE_CATEGORY_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;

/** Cambio de `asset_settings` que manda la pantalla (`PUT /assetSettings`). */
export interface AssetSettingsInput {
  financeSync?: FinanceSyncMode;
  expenseCategory?: string;
  maintenanceExpenseCategory?: string;
  usefulLifeMonths?: Partial<Record<AssetCategory, number | null>>;
}

/**
 * Valida un cambio de configuración. Sólo devuelve lo que llegó; una vida útil
 * en `null` quita la de esa categoría (vuelve a la de omisión).
 */
export function validateSettingsInput(raw: unknown): ValidationResult<AssetSettingsInput> {
  const errors: ValidationIssue[] = [];
  const out: AssetSettingsInput = {};
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return { ok: false, errors: [{ field: "body", code: "no_valido" }] };
  const r = raw as Record<string, unknown>;
  if (r.financeSync !== undefined) {
    if (isFinanceSyncMode(r.financeSync)) out.financeSync = r.financeSync;
    else errors.push({ field: "financeSync", code: "no_valido" });
  }
  for (const k of ["expenseCategory", "maintenanceExpenseCategory"] as const) {
    if (r[k] === undefined) continue;
    const v = typeof r[k] === "string" ? (r[k] as string).trim() : "";
    if (EXPENSE_CATEGORY_RE.test(v)) out[k] = v;
    else errors.push({ field: k, code: "no_valido" });
  }
  if (r.usefulLifeMonths !== undefined) {
    const life = r.usefulLifeMonths;
    if (typeof life !== "object" || life === null || Array.isArray(life)) errors.push({ field: "usefulLifeMonths", code: "no_valido" });
    else {
      const clean: Partial<Record<AssetCategory, number | null>> = {};
      for (const [cat, v] of Object.entries(life as Record<string, unknown>)) {
        if (!isAssetCategory(cat)) errors.push({ field: `usefulLifeMonths.${cat}`, code: "no_valido" });
        else if (v === null) clean[cat] = null;
        else if (typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= MAX_USEFUL_LIFE_MONTHS) clean[cat] = v;
        else errors.push({ field: `usefulLifeMonths.${cat}`, code: "fuera_de_rango" });
      }
      out.usefulLifeMonths = clean;
    }
  }
  return errors.length ? { ok: false, errors } : { ok: true, value: out };
}

/** Notas de `finance.note`: por qué una compra no está (o dejó de estar) al día en Finanzas. */
export const FINANCE_NOTES = [
  "integracion_apagada",
  "total_cambiado",
  "costo_cambiado",
  "gasto_confirmado",
  "gasto_eliminado",
  "sin_monto",
  "error",
] as const;
export type FinanceNote = (typeof FINANCE_NOTES)[number];

export const FINANCE_NOTE_LABELS: Record<FinanceNote, Label> = {
  integracion_apagada: {
    es: "La integración con Finanzas no está encendida para tu empresa: el costo se queda en Activos.",
    en: "The Finance integration isn't on for your company: the cost stays in Assets.",
  },
  total_cambiado: {
    es: "El total cambió después de ligar el gasto: Finanzas revisa la diferencia.",
    en: "The total changed after the expense was linked: Finance reviews the difference.",
  },
  costo_cambiado: {
    es: "El costo de una pieza cambió después de ligar el gasto: Finanzas revisa la diferencia.",
    en: "An item's cost changed after the expense was linked: Finance reviews the difference.",
  },
  gasto_confirmado: {
    es: "Finanzas ya confirmó el gasto: los cambios de la compra no lo modifican.",
    en: "Finance already confirmed the expense: changes to the purchase don't modify it.",
  },
  gasto_eliminado: {
    es: "Finanzas borró o separó el gasto: Activos no lo vuelve a crear.",
    en: "Finance deleted or separated the expense: Assets doesn't create it again.",
  },
  sin_monto: {
    es: "La compra se quedó sin monto: se desligó del gasto (el gasto no se borró).",
    en: "The purchase has no amount anymore: it was unlinked from the expense (the expense wasn't deleted).",
  },
  error: {
    es: "No se pudo integrar con Finanzas; se reintenta en la siguiente sincronización.",
    en: "It couldn't be integrated with Finance; it's retried on the next sync.",
  },
};

export const isFinanceNote = (v: unknown): v is FinanceNote => typeof v === "string" && (FINANCE_NOTES as readonly string[]).includes(v);

/** Tipo de origen del gasto que crea Activos (`sourceRef.type`). */
export const ASSET_PURCHASE_SOURCE_REF = "asset_purchase";
/** `source` del gasto en `@torre/expenses` (no se importa el paquete: cero dependencias). */
export const ASSET_EXPENSE_SOURCE = "activos";

/** Monto de la compra que va a Finanzas: el total o, si no hay, el subtotal. */
export function purchaseAmount(p: Pick<AssetPurchase, "total" | "subtotal">): number | null {
  const v = p.total ?? p.subtotal;
  return v == null || !Number.isFinite(Number(v)) ? null : Number(v);
}

/** ¿La compra tiene un monto que integrar? */
export const purchaseHasAmount = (p: Pick<AssetPurchase, "total" | "subtotal">): boolean => purchaseAmount(p) != null;

/**
 * ¿Esta compañía se puede integrar? Donde Finanzas es interna de la plataforma
 * (`internalFinance`), sólo la compañía de Gastos (`financeCompanyId`); sin ella
 * configurada, ninguna. En las demás plataformas, cualquiera que lo encienda.
 */
export function financeSyncAllowed(companyId: string, opts: { internalFinance: boolean; financeCompanyId?: string | null }): boolean {
  if (!opts.internalFinance) return true;
  return Boolean(opts.financeCompanyId) && companyId === opts.financeCompanyId;
}

/**
 * `financeSync` cuando la compañía no guardó nada: encendido (`borrador`) sólo
 * para la compañía de Gastos de una plataforma con Finanzas interna (en
 * CC-CONSOLE, Coding Cloud). Todas las demás nacen apagadas.
 */
export function defaultFinanceSync(companyId: string, opts: { internalFinance: boolean; financeCompanyId?: string | null }): FinanceSyncMode {
  return opts.internalFinance && financeSyncAllowed(companyId, opts) ? "borrador" : "off";
}

/** Adjunto de Activos tal como lo lleva el gasto: compartido (el archivo vive en Activos). */
export interface AssetExpenseAttachment {
  kind: "factura_pdf" | "factura_xml";
  path: string;
  name: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  shared: true;
  fromAsset: true;
}

/** Lo que Activos le pide a Gastos para crear o actualizar el gasto de una compra. */
export interface PurchaseExpenseDraft {
  expenseId: string;
  purchaseId: string;
  date: string;
  amount: number;
  currency: string;
  exchangeRate: number | null;
  vendor: string;
  category: string;
  description: string;
  invoice: InvoiceRef | null;
  attachments: AssetExpenseAttachment[];
}

/**
 * Borrador del gasto de una compra (o `null` si no tiene monto). La fecha es la
 * de la compra (D4); sin fecha, la de hoy. Sólo las facturas (PDF/XML) viajan
 * como adjuntos compartidos.
 */
export function purchaseExpenseDraft(
  purchase: Pick<AssetPurchase, "date" | "currency" | "exchangeRate" | "subtotal" | "total" | "invoice" | "attachments" | "itemCount"> & { id: string },
  opts: { supplierName?: string | null; category: string; assetNames?: readonly string[]; today: string; uploadedAt?: string },
): PurchaseExpenseDraft | null {
  const amount = purchaseAmount(purchase);
  if (amount == null) return null;
  const names = (opts.assetNames || []).map((n) => String(n || "").trim()).filter(Boolean);
  const count = Math.max(purchase.itemCount || 0, names.length);
  const shown = names.slice(0, 3).join(", ");
  const more = names.length > 3 ? "…" : "";
  const pieces = `${count} ${count === 1 ? "pieza" : "piezas"}`;
  const uuid = purchase.invoice && purchase.invoice.uuid ? String(purchase.invoice.uuid).toUpperCase() : null;
  const invoice: InvoiceRef | null =
    purchase.invoice && (uuid || purchase.invoice.serie || purchase.invoice.folio)
      ? { uuid, serie: purchase.invoice.serie ?? null, folio: purchase.invoice.folio ?? null }
      : null;
  const attachments = (purchase.attachments || [])
    .filter((a: AssetAttachment) => a && (a.kind === "factura_pdf" || a.kind === "factura_xml") && a.path)
    .map(
      (a: AssetAttachment): AssetExpenseAttachment => ({
        kind: a.kind as "factura_pdf" | "factura_xml",
        path: a.path,
        name: a.name,
        contentType: a.contentType || (a.kind === "factura_xml" ? "application/xml" : "application/pdf"),
        size: a.size || 0,
        uploadedAt: opts.uploadedAt || opts.today,
        shared: true,
        fromAsset: true,
      }),
    );
  const currency = String(purchase.currency || BASE_CURRENCY).toUpperCase();
  return {
    expenseId: purchaseExpenseId(purchase.id),
    purchaseId: purchase.id,
    date: purchase.date || opts.today,
    amount,
    currency,
    exchangeRate: currency === BASE_CURRENCY ? 1 : purchase.exchangeRate ?? null,
    vendor: String(opts.supplierName || "").trim() || "Proveedor sin nombre",
    category: opts.category,
    description: shown ? `Activos: ${shown}${more} (${pieces})` : `Compra de activos (${pieces})`,
    invoice,
    attachments,
  };
}
